import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase/client/supabaseClient';

export interface KYCDocument {
  id: string;
  file: File;
  preview: string;
  type: 'aadhaar' | 'pan_card' | 'bank_proof' | 'passport_photo';
  uploaded: boolean;
  path?: string;
}

export interface KYCFormData {
  upi_id: string;
  owner_name: string;
  gst_number: string;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  branch_name: string;
}

interface KYCStore {
  currentStep: number;
  totalSteps: number;
  formData: KYCFormData;
  documents: KYCDocument[];
  shopAccountId: string | null; 
  isAuthenticated: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  uploadProgress: number;
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateFormData: (data: Partial<KYCFormData>) => void;
  addDocument: (file: File, type: KYCDocument['type']) => void;
  removeDocument: (id: string) => void;
  setShopAccountId: (id: string) => void;
  setIsAuthenticated: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  setIsSubmitting: (value: boolean) => void;
  setUploadProgress: (value: number) => void;
  clearStore: () => void;
  uploadDocuments: () => Promise<boolean>;
  submitKYCForm: () => Promise<boolean>;
}

const initialFormData: KYCFormData = {
  owner_name: '',
  gst_number: '',
  account_holder_name: '',
  account_number: '',
  ifsc_code: '',
  bank_name: '',
  branch_name: '',
  upi_id: ''
};

const useKYCStore = create<KYCStore>()(
  persist(
    (set, get) => ({
      currentStep: 0,
      totalSteps: 4, // Authentication, Personal Info, Bank Details, Document Upload
      formData: initialFormData,
      documents: [],
      shopAccountId: null,
      isAuthenticated: false,
      isLoading: false,
      isSubmitting: false,
      uploadProgress: 0,

      setCurrentStep: (step) => set({ currentStep: step }),
      
      nextStep: () => {
        const { currentStep, totalSteps } = get();
        if (currentStep < totalSteps - 1) {
          set({ currentStep: currentStep + 1 });
        }
      },
      
      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
          set({ currentStep: currentStep - 1 });
        }
      },
      
      updateFormData: (data) => set((state) => ({ 
        formData: { ...state.formData, ...data } 
      })),
      
      addDocument: (file, type) => {
        // Create URL for preview
        const preview = URL.createObjectURL(file);
        
        // Check if a document of this type already exists, replace it
        set((state) => {
          const filteredDocs = state.documents.filter(doc => doc.type !== type);
          
          return {
            documents: [
              ...filteredDocs,
              {
                id: uuidv4(),
                file,
                preview,
                type,
                uploaded: false
              }
            ]
          };
        });
      },
      
      removeDocument: (id) => set((state) => ({
        documents: state.documents.filter(doc => doc.id !== id)
      })),
      
      setShopAccountId: (id) => set({ shopAccountId: id }), 
      
      setIsAuthenticated: (value) => set({ isAuthenticated: value }),
      
      setIsLoading: (value) => set({ isLoading: value }),
      
      setIsSubmitting: (value) => set({ isSubmitting: value }),
      
      setUploadProgress: (value) => set({ uploadProgress: value }),
      
      clearStore: () => set({
        currentStep: 0,
        formData: initialFormData,
        documents: [],
        shopAccountId: null, 
        isAuthenticated: false,
        isLoading: false,
        isSubmitting: false,
        uploadProgress: 0
      }),
      
      uploadDocuments: async () => {
        const { documents, shopAccountId, setUploadProgress } = get(); 
        
        if (!shopAccountId) {
          console.error('Upload failed: No shop account ID available');
          return false;
        }
        
        let uploadedCount = 0;
        const totalUploads = documents.length;
        let allUploaded = true;
        let updatedDocuments = [...documents]; // Create a copy to track updated documents
        
        console.log(`Starting upload of ${totalUploads} documents for shop ${shopAccountId}`);
        
        // Process each document
        for (let i = 0; i < documents.length; i++) {
          const document = documents[i];
          try {
            // Create file path with format: shopAccountId-timestamp-doctype
            const timestamp = Date.now();
            const filePath = `${shopAccountId}-${timestamp}-${document.type}`;
            
            // Determine bucket based on document type
            let bucket = '';
            if (document.type === 'aadhaar') {
              bucket = 'aadhar';
            } else if (document.type === 'passport_photo') {
              bucket = 'passport-photos';
            } else if (document.type === 'bank_proof') {
              bucket = 'bank-proof';
            } else if (document.type === 'pan_card') {
              bucket = 'pan';
            }
            
            if (!bucket) {
              console.error(`Invalid document type: ${document.type}`);
              allUploaded = false;
              continue;
            }
            
            console.log(`Uploading ${document.type} to bucket: ${bucket}, path: ${filePath}`);
            
            // Upload file to storage
            const { error: uploadError, data } = await supabase.storage
              .from(bucket)
              .upload(filePath, document.file);
              
            if (uploadError) {
              console.error(`Error uploading ${document.type}:`, uploadError);
              console.error(`Upload details - Bucket: ${bucket}, Path: ${filePath}, File size: ${document.file.size}`);
              allUploaded = false;
              continue;
            }
            
            console.log(`Successfully uploaded ${document.type} to ${bucket}/${filePath}`);
            
            // Update the document in our local array with path and uploaded status
            updatedDocuments[i] = {
              ...updatedDocuments[i],
              uploaded: true,
              path: filePath
            };
            
            uploadedCount++;
            setUploadProgress(Math.floor((uploadedCount / totalUploads) * 100));
            
          } catch (error) {
            console.error(`Unexpected error uploading ${document.type}:`, error);
            console.error('Document details:', {
              id: document.id,
              type: document.type,
              fileName: document.file?.name,
              fileSize: document.file?.size
            });
            allUploaded = false;
          }
        }
        
        // Update the documents in the store with the new paths
        set({ documents: updatedDocuments });
        
        // Log the updated documents to verify paths are set
        console.log('Updated documents with paths:', updatedDocuments.map(doc => ({
          type: doc.type,
          path: doc.path,
          uploaded: doc.uploaded
        })));
        
        console.log(`Upload process completed. Success: ${allUploaded}`);
        return allUploaded;
      },
      
      submitKYCForm: async () => {
        const { formData, documents, shopAccountId, setIsSubmitting } = get();
        
        if (!shopAccountId) {
          console.error('KYC submission failed: No shop account ID available');
          return false;
        }
        
        try {
          setIsSubmitting(true);
          console.log('Starting KYC submission process for shop', shopAccountId);
          
          // First upload all documents
          console.log('Starting document upload process');
          const docsUploaded = await get().uploadDocuments();
          if (!docsUploaded) {
            console.error('KYC submission aborted: Document upload failed');
            return false;
          }
          
          // Get fresh documents from the store after upload
          const updatedDocuments = get().documents;
          
          // Prepare document paths by type
          const docPaths: Record<string, string> = {};
          updatedDocuments.forEach(doc => {
            console.log(`Processing document: ${doc.type}, uploaded: ${doc.uploaded}, path: ${doc.path}`);
            if (doc.path) {
              if (doc.type === 'aadhaar') {
                docPaths.aadhaar = doc.path;
              } else {
                docPaths[`${doc.type}_path`] = doc.path;
              }
            }
          });
          
          console.log('Document paths prepared:', docPaths);
          console.log('Documents count:', updatedDocuments.length);
          console.log('Documents with path:', updatedDocuments.filter(d => !!d.path).length);
          console.log('Submitting KYC information to API');
          
          // Call the RPC function to update KYC info - now including p_upi_id
          const { data, error } = await supabase
          .schema('api')
          .rpc('update_shop_kyc_info', {
            p_shop_id: shopAccountId,
            p_owner_name: formData.owner_name,
            p_gst_number: formData.gst_number || '',
            p_aadhaar: docPaths.aadhaar || '',
            p_pan_card_path: docPaths.pan_card_path || '',
            p_bank_proof_path: docPaths.bank_proof_path || '',
            p_passport_photo_path: docPaths.passport_photo_path || '',
            p_account_holder_name: formData.account_holder_name || '',
            p_account_number: formData.account_number || '',
            p_ifsc_code: formData.ifsc_code || '',
            p_bank_name: formData.bank_name || '',
            p_branch_name: formData.branch_name || '',
            p_upi_id: formData.upi_id || '' // Add the upi_id parameter
          });
          
          if (error) {
            console.error('Error from RPC call to update_shop_kyc_info:', error);
            console.error('RPC params:', {
              shopAccountId,
              owner_name: formData.owner_name,
              // Include UPI in logs but omit other sensitive data
              upi_id: !!formData.upi_id,
              documents: Object.keys(docPaths)
            });
            return false;
          }
          
          console.log('KYC submission successful, advancing to success step');
          // Directly set to step 4 (success step)
          set({ currentStep: 4 });
          console.log('Current step updated to:', get().currentStep);
          return true;
        } catch (error) {
          console.error('Unexpected error during KYC submission:', error);
          console.error('Form data state:', {
            shopAccountId,
            owner_name: formData.owner_name,
            hasUpiId: !!formData.upi_id,
            documentCount: documents.length,
            uploadedDocuments: documents.filter(doc => doc.uploaded).length
          });
          return false;
        } finally {
          setIsSubmitting(false);
        }
      }
    }),
    {
      name: 'kyc-storage'
    }
  )
);

// Add a new function to clear storage and reset store after successful KYC submission
export const clearKYCStore = () => {
  // Get the current store state
  const currentState = useKYCStore.getState();
  const shopAccountId = currentState.shopAccountId;
  
  // Clear the persisted state from localStorage
  localStorage.removeItem('kyc-storage');
  
  // Reset the store state while preserving shopAccountId
  useKYCStore.getState().clearStore();
  
  // If we had a shopAccountId, restore it
  if (shopAccountId) {
    useKYCStore.getState().setShopAccountId(shopAccountId);
    useKYCStore.getState().setIsAuthenticated(true);
  }
  
  // Set the step to 4 (success step) if we preserved authentication
  if (shopAccountId) {
    useKYCStore.getState().setCurrentStep(4);
  }
};

export default useKYCStore;
